import { RefObject, useEffect } from 'react';
import { getTriggerForMenu } from '../utils/dropdownRegistry';

export const useClickOutside = (
  refs: RefObject<HTMLElement> | RefObject<HTMLElement>[],
  handleOnClickOutside: (event: MouseEvent | TouchEvent) => void,
  isOpen: boolean = false,
  isDialog?: boolean
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const refsArray = Array.isArray(refs) ? refs : [refs];
      // If the click was inside a portaled dropdown menu element, and that
      // menu is one of the registered menu refs, treat it as an inside click.
      // This avoids treating clicks inside portaled menus as outside clicks.
      try {
        const targetEl = event.target as HTMLElement | null;
        if (targetEl && targetEl.closest) {
          const menuEl = targetEl.closest('[data-dropdown-menu-horizontal]') as HTMLElement | null;
          if (menuEl) {
            // Find the trigger element registered for this menu (if any)
            const triggerEl = getTriggerForMenu(menuEl);
            const isRelevantMenu = refsArray.some((ref) => {
              const current = ref.current as HTMLElement | null;
              // If the registered trigger for this menu is inside one of the
              // refs (or the refs contain the menu itself), treat click as inside.
              return !!(
                (triggerEl && current && (current === triggerEl || current.contains(triggerEl))) ||
                (current && (current === menuEl || current.contains(menuEl)))
              );
            });
            if (isRelevantMenu) return;
          }
        }
      } catch (e) {
        // ignore and continue to regular checks
      }

      let isInsideAnyRef = false;
      if (isDialog) {
        isInsideAnyRef = refsArray.some(ref => {
          const el = ref.current;
          if (!el) return false;
      
          const { left, right, top, bottom } = el.getBoundingClientRect(); 
          
          // For touch events(mobile), consider the first touch point, for mouse events(desktop), consider the mouse point
          const clientX = (event as TouchEvent).touches?.[0]?.clientX ?? (event as MouseEvent).clientX;
          const clientY = (event as TouchEvent).touches?.[0]?.clientY ?? (event as MouseEvent).clientY;          
          
          // Check if the click is within the dialog bounds to include scrollbars as inside
          // Click x should be between left and right, and y should be between top and bottom
          return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
        });
      } else {
        isInsideAnyRef = refsArray.some((ref) =>
          ref.current?.contains(event.target as Node)
        );
      }
      if (isInsideAnyRef) {
        return;
      }
      handleOnClickOutside(event);
    };

    if (isOpen) {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
    }
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [refs, handleOnClickOutside, isOpen]);
}